import moment from 'moment';

import axios from './axios';
import { LOCALE } from '../constants/Values';
import { getCookie } from '../util/cookies';
import { getUserLocale } from '../util/userLocale';

const timeZoneOffset = moment().utcOffset() / 60;

/**
 * @api post /auth/terms Accept terms and conditions
 * @param {String} accessToken accessToken received from the backend
 * @param agreementVersion
 * @return {Promise}
 * */
export const acceptTermsAndConditions = (accessToken, agreementVersion) =>
  axios.post('/auth/terms', {
    accessToken,
    agreementVersion,
  });

/**
 * @api post /auth/signin/confirm Request signin confirm
 * @param {String} accessToken accessToken received from the backend
 * @param {String} temporaryPassword temporaryPassword received via SMS/Email
 * @return {Promise}
 * */
export const confirmSignIn = (accessToken, temporaryPassword) =>
  axios.post('/auth/signin/confirm', {
    accessToken,
    temporaryPassword,
  });

/**
 * @api post /auth/signup/confirm Request signup confirm
 * @param {String} identifier User identifier (phone number / email)
 * @param {String} password User password
 * @param {String} code Verification code received via SMS/Email
 * @return {Promise}
 * */
export const confirmSignUp = (identifier, password, code) =>
  axios.post('/auth/signup/confirm', {
    clientType: 'WEB',
    code,
    locale: getCookie(LOCALE) || getUserLocale(),
    password,
    timeZoneOffset,
    username: identifier,
  });

/**
 * @api get /auth/agreement Request latest terms and conditions
 * @return {Promise}
 * */
export const getTermsAndConditions = () => axios.get('/auth/agreement');

/**
 * @api post /auth/password/reset Reset user password
 * @param {String} login User identifier (phone number / email)
 * @return {Promise}
 * */
export const resetPassword = login =>
  axios.post('/auth/password/reset', {
    login,
  });

/**
 * @api post /auth/password/confirm Complete password reset and set new password
 * @param {String} login User identifier (phone number / email)
 * @param {String} code Verification code received via SMS/Email
 * @param {String} password New user password
 * @return {Promise}
 * */
export const setNewPassword = (login, code, password) =>
  axios.post('/auth/password/confirm', {
    login,
    code,
    password,
  });

/**
 * @api post /auth/signin Request user signin
 * @param {String} identifier User identifier (phone number / email)
 * @param {String} password User password
 * @return {Promise}
 * */
export const signIn = (identifier, password) =>
  axios.post('/auth/signin', {
    login: identifier,
    clientType: 'WEB',
    locale: getCookie(LOCALE) || getUserLocale(),
    password,
    timeZoneOffset,
  });

/**
 * @api post /auth/signout User signout
 * @return {Promise}
 */
export const signOutUser = () => axios.post('/auth/signout');

/**
 * @api post /auth/signup Request user signup
 * @param {String} identifier User identifier (phone number / email)
 * @return {Promise}
 * */
export const signUp = identifier =>
  axios.post('/auth/signup', {
    username: identifier,
  });

/**
 * @api post /auth/signup/verify Request signup verify
 * @param {String} identifier User identifier (phone number / email)
 * @param {String} code Verification code received via SMS/Email
 * @return {Promise}
 * */
export const verifySignUp = (identifier, code) =>
  axios.post('/auth/signup/verify', {
    username: identifier,
    code,
  });

/**
 * @api post /auth/users/verify Request user existence
 * @param {String} identifier User identifier (phone number / email)
 * @return {Promise}
 * */
export const verifyUserExistence = identifier =>
  axios.post('/auth/users/verify', {
    login: identifier,
  });

/**
 * @api post /auth/verify Request user registration
 * @param {String} identifier User identifier (phone number / email)
 * @return {Promise}
 * */
export const verifyUserRegistration = identifier =>
  axios.post('/auth/verify', {
    login: identifier,
  });
